/**
 * @file gDotObjInject.js
 * @description inserts a gDotObj into the target 
 *              annotation.variantObj
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * @author don michael
 * @since April 15, 2025
 */
import { objectDeepCopy, objectExists, objectKeyExists, stringExists } from 'src/@common/utilities';

// helper func
function initVariantObj(variantObj, gDotObj, pDotObj) {

    // inits the variantObj in-place using the gDotObj
    if (objectExists(variantObj) &&
        objectExists(gDotObj)) {

        if (objectKeyExists(variantObj, 'gDotObj')) {

            if (objectKeyExists(gDotObj, 'hg19') &&
                objectExists(gDotObj.hg19)) {
                variantObj.gDotObj.hg19 = objectDeepCopy(gDotObj.hg19);
            }

            if (objectKeyExists(gDotObj, 'hg38') &&
                objectExists(gDotObj.hg38)) {
                variantObj.gDotObj.hg38 = objectDeepCopy(gDotObj.hg38);
            }

            if (objectKeyExists(gDotObj, 'source')) {
                variantObj.gDotObj.source = gDotObj.source;
            }

            if (objectKeyExists(gDotObj, 'hgvsVer')) {
                variantObj.gDotObj.hgvsVer = gDotObj.hgvsVer;
            }

            if (objectKeyExists(gDotObj, 'cdotVer')) {
                variantObj.gDotObj.cdotVer = gDotObj.cdotVer;
            }
        }

        if (objectKeyExists(variantObj, 'pDotObj')) {

            if (objectKeyExists(pDotObj, 'hg38') &&
                objectExists(pDotObj.hg38)) {
                variantObj.pDotObj.hg38 = objectDeepCopy(pDotObj.hg38);
            }

            if (objectKeyExists(pDotObj, 'source')) {
                variantObj.pDotObj.source = pDotObj.source;
            }

            if (objectKeyExists(pDotObj, 'hgvsVer')) {
                variantObj.pDotObj.hgvsVer = pDotObj.hgvsVer;
            }

            if (objectKeyExists(pDotObj, 'cdotVer')) {
                variantObj.pDotObj.cdotVer = pDotObj.cdotVer;
            }
        }
    }
}

function gDotObjInject(
    targetAnnotationId,
    targetVariantObjId,
    gDotObj,
    pDotObj,
    annotationGroup) {

    const annotationGroupCopy = objectDeepCopy(annotationGroup);

    if (objectExists(annotationGroupCopy)) {

        if (stringExists(targetAnnotationId) &&
            stringExists(targetVariantObjId) &&
            objectExists(gDotObj)) {

            // find the target annotationObj in the annotationGroup
            const targetAnnotationObj = annotationGroupCopy.annotationObjs.find((item) => {

                if (objectKeyExists(item, 'annotation') &&
                    objectExists(item.annotation)) {
                    return item.annotation._id === targetAnnotationId;
                } else {
                    return false;
                }

            });

            if (objectExists(targetAnnotationObj)) {

                // find the annotation in the targetAnnotationObj
                if (objectKeyExists(targetAnnotationObj, 'annotation') &&
                    objectExists(targetAnnotationObj.annotation)) {

                    const targetAnnotation = targetAnnotationObj.annotation;

                    // the target variantObj may exist as: 
                    // 1.  as a single object ie, annotation.fields.variantObj
                    // 2.  as an element in annotation.fields.variantObjArray.variantObj

                    if (objectKeyExists(targetAnnotation, 'fields') &&
                        objectExists(targetAnnotation.fields)) {

                        // look for variantObj as an element in fields.variantObjArray
                        if (objectKeyExists(targetAnnotation.fields, 'variantObjArray')) {

                            const foundIndex = targetAnnotation.fields.variantObjArray.findIndex((item) => {
                                return item._id === targetVariantObjId;
                            });

                            if (foundIndex !== -1) {
                                initVariantObj(targetAnnotation.fields.variantObjArray[foundIndex], gDotObj, pDotObj);
                                targetAnnotationObj.dirty = true;
                            }

                        } else {
                            // look for variantObj as fields.variantObj
                            if (objectKeyExists(targetAnnotation.fields, 'variantObj') &&
                                objectExists(targetAnnotation.fields.variantObj)) {

                                initVariantObj(targetAnnotation.fields.variantObj, gDotObj, pDotObj);
                                targetAnnotationObj.dirty = true;
                            }
                        }

                    }
                }
            }
        }
    }

    return annotationGroupCopy;
}

export default gDotObjInject;